import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import browser from 'browser-detect';
import moment from 'moment-timezone';
import streamSaver from 'drzzle-streamsaver';
import '@/modules/file-stream/zip-stream';
import { modals } from '@/mixins/modal';
import dropsLoader from '@/mixins/components/drops-loader';
import { guid } from '@/mixins/guid';
import { siteBuilder } from '@/mixins/site-builder';
import globalTabs from '@/mixins/components/tabs';
import ImportSite from '@storm/import-site';
import RawSiteData from '@storm/admin/raw-site-data';
import * as clipboard from 'clipboard-polyfill/dist/clipboard-polyfill.promise';
import SuspendSite from './suspend';
import DeleteSite from './delete';
import Domains from './domains';
import Forms from './forms';
import Options from './options';
import Settings from './settings';
import Forecast from './forecast';
import LeaveCollab from './leave-collab';

const streams = require('web-streams-polyfill/ponyfill');

export default Vue.component('admin-edit-site', {
  template: require('./template.html'),
  mixins: [modals, guid, siteBuilder],
  name: 'admin-edit-site',
  computed: {
    ...mapGetters([
      'websites',
      'currentSite',
      'account',
      'siteBuilt',
      'stormUser',
    ]),
    descWarning() {
      let color = '';
      if (this.descCount > this.descLimit - 10 && this.descCount < this.descLimit - 1) {
        color = '#f98d40';
      }
      if (this.descCount >= this.descLimit - 1) {
        color = '#ff5f5f';
      }
      return color;
    },
  },
  components: {
    ImportSite,
    SuspendSite,
    DeleteSite,
    RawSiteData,
    Domains,
    Forms,
    Options,
    Settings,
    globalTabs,
    LeaveCollab,
    dropsLoader,
  },
  data() {
    return {
      loading: false,
      descCount: null,
      descLimit: 160,
      typing: false,
      smallLoaders: {
        sitename: false,
        analytics: false,
        cache: false,
        description: false,
      },
      thumbLoading: false,
      siteThumb: false,
      initial: {},
      initialEdits: {
        sitename: '',
        analytics: '',
        description: '',
      },
      sitename: '',
      description: '',
      analytics: '',
      copiedUrl: false,
      modals: {
        deleteSite: false,
        suspendSite: false,
        importSite: false,
        rawSiteData: false,
        collaborators: false,
      },
      siteData: {
        sitename: false,
        analytics: false,
        description: false,
      },
      buttonLoaders: {
        download: false,
      },
      downloadProgress: 0,
      browser: null,
      tabs: [],
    };
  },
  created() {
    // set correct site if not already
    // route id (from page)
    const _id = this.$router.currentRoute.params.id;
    if (_id !== this.currentSite._id || !this.currentSite._id) {
      const site = _.find(this.websites, { _id });
      if (site) {
        this.$store.dispatch('setCurrentSite', site);
      } else {
        // if site from route does not exist, send to all sites
        this.$router.push({ path: '/websites' });
      }
    }
    this.browser = browser().name;
    this.loadThumbnail();
    this.sitename = this.currentSite.alias;
    this.description = this.currentSite.description || '';
    this.analytics = !this.currentSite.analytics ? '' : this.currentSite.analytics;
    this.initial = _.cloneDeep(this.currentSite);
    this.setTabs();
  },
  mounted() {
    // used for the modal mixin
    this.cls = '.adminUi-modalOverlay';
  },
  watch: {
    'currentSite.lifecycle': function setLifecycle() {
      this.setTabs();
    },
  },
  methods: {
    loadThumbnail() {
      const { meta = {} } = this.currentSite;
      this.siteThumb = meta.image ? this._servePrefix(meta.image) + meta.image : false;
    },
    noChanges(type) {
      return this.initialEdits[type] === this[type];
    },
    toggleEditData(type) {
      this.siteData[type] = !this.siteData[type];
      if (this.siteData[type]) {
        this.initialEdits.sitename = _.clone(this.sitename);
        this.initialEdits.analytics = _.clone(this.analytics);
        this.initialEdits.description = _.clone(this.description);
      }
      if (!this.siteData[type]) {
        this[type] = this.initialEdits[type];
      }
    },
    async clearCache() {
      this.smallLoaders.cache = true;
      const notif = {
        id: this.guid(),
        type: 'success',
        message: 'Website Cache Successfully Cleared!',
      };
      try {
        await this.$lightning([{
          name: 'clearSiteCache',
          payload: {
            siteId: this.currentSite._id,
          },
        }]);
      } catch (e) {
        this.$log.error('error: ', e);
        notif.type = 'error';
        notif.message = `${e.message || e}. This has been reported.`;
        await this._reportError({
          error: e,
          fn: 'clearCache',
        });
      } finally {
        this.smallLoaders.cache = false;
        this.$store.dispatch('addNotification', notif);
        this._trackEvent({
          type: 'clear_cache_from_admin',
          meta: {
            site_name: this.currentSite.alias,
            site_type: this.currentSite.type,
            site_pages: this.currentSite.pages.length,
            site_templates: this.currentSite.templates.length,
            site_draft: this.currentSite.drafts.length,
          },
        });
      }
    },
    async createZip(siteFiles) {
      const total = siteFiles.length;
      let done = 0;
      const timestamp = moment(Date.now()).format('MM-DD-YYYY_hh:mm:ss:a');
      streamSaver.WritableStream = streams.WritableStream;
      const fileStream = streamSaver.createWriteStream(`${this.currentSite.name}_${timestamp}.zip`);
      // download files using fetch and add to zip stream
      const streamList = [];
      await Promise.all(siteFiles.map(f => new Promise(async (resolve, reject) => {
        const siteFolder = new RegExp(`${this.currentSite.name}/`, 'gi');
        const name = f.split(siteFolder)[1];
        try {
          const urlFile = await fetch(`${process.env.S3_WEBSITE_BUCKET_URL}/${f}`, {
            method: 'GET',
            // no-cache is very important here https://stackoverflow.com/questions/55158189/cross-origin-requests-ajax-requests-to-aws-s3-sometimes-result-in-cors-error
            // as s3 has a CORs bug trying to use cached assets
            cache: 'no-cache',
            headers: {
              Origin: window.location.origin,
            },
          });
          const fileToStream = {
            name, // we can add a sub folder if needed
            stream: () => urlFile.body,
          };
          streamList.push(fileToStream);
          done += 1;
          this.downloadProgress = ((done / total) * 100).toFixed(0);
          resolve(fileToStream);
        } catch (e) {
          reject(`${e.message || e}. You may need to refresh the page and try again`);
        }
      })));

      const ZipStream = window.ZipStream;
      const readableZipStream = new ZipStream({
        start(ctrl) {
          _.forEach(streamList, (item) => {
            ctrl.enqueue(item);
          });
          ctrl.close();
        },
      });
      // more optimized
      // if (window.WritableStream && readableZipStream.pipeTo) {
      //   // TODO currently .pipeTo doesn't seem to be available in edge
      //   // therefore this promise does not finish and just hangs even
      //   // with the polyfill. the pump also infinitly loops if ran.
      //   return readableZipStream
      //     .pipeTo(fileStream).then(() => this.$log.debug('done creating zip'))
      //     .catch(e => this.$log.error(e.message || e));
      // }
      // less optimized
      const writer = fileStream.getWriter();
      const reader = readableZipStream.getReader();
      const pump = () => reader.read()
        .then((res) => {
          if (res.done) {
            writer.close();
          } else {
            writer.write(res.value).then(pump);
          }
        });
      this.downloadProgress = 0;
      return pump();
    },
    async getFileList() {
      return this._SEND([{
        name: 'fetchUserFiles',
        payload: {
          folder: `${this._sitePath}/`,
          recursive: true,
        },
      }]);
    },
    async downloadSite() {
      if (!this.browser.match(/edge/i)) {
        this.buttonLoaders.download = true;
        const notif = {
          id: this.guid(),
          type: 'success',
          message: 'Download complete!',
        };
        if (this.browser.match(/firefox|safari/i)) {
          notif.message = 'Assets downloaded. Starting the zip process!';
        }
        try {
          const s3Files = await this.getFileList();
          await this.createZip(s3Files);
        } catch (e) {
          this.$log.error('error: ', e);
          notif.type = 'error';
          notif.message = `${e.message || e}. This has been reported.`;
          await this._reportError({
            error: e,
            fn: 'downloadSite',
          });
        } finally {
          this.buttonLoaders.download = false;
          this.$store.dispatch('addNotification', notif);
          this._trackEvent({
            type: 'download_site',
            meta: {
              site_name: this.currentSite.alias,
              site_type: this.currentSite.type,
              site_pages: this.currentSite.pages.length,
              site_templates: this.currentSite.templates.length,
              site_draft: this.currentSite.drafts.length,
            },
          });
        }
      }
    },
    saveDescription() {
      this.$validator.validate('description', this.description).then(async (result) => {
        if (result && this.description !== this.initialEdits.description) {
          this.smallLoaders.description = true;
          const notif = {
            id: this.guid(),
            type: 'success',
            message: 'Changes Successfully Saved!',
          };
          try {
            this.$store.dispatch('setGlobalValue', {
              item: this.currentSite,
              key: 'description',
              value: this.description,
            });
            // save to db
            await this.$lightning([{
              name: 'updateSiteAttr',
              payload: {
                siteId: this.currentSite._id,
                attr: 'description',
                value: this.description,
              },
            }]);
            await this.callBuilder({
              publish: 'site',
              site: {
                _id: this.currentSite._id,
              },
            });
            this.initialEdits.description = _.clone(this.description);
            this.toggleEditData('description');
          } catch (e) {
            this.$log.error('error: ', e);
            notif.type = 'error';
            notif.message = `${e.message || e}. This has been reported.`;
            await this._reportError({
              error: e,
              fn: 'saveDescription',
            });
          } finally {
            this.smallLoaders.description = false;
            this.$store.dispatch('addNotification', notif);
          }
        }
      });
    },
    inputText() {
      this.typing = true;
      this.charCount();
    },
    pasteText() {
      this.typing = true;
      clipboard.readText().then(() => {
        this.charCount();
        // prevent users from pasting in copy longer than limit
        clearTimeout(this.pasteTO);
        this.pasteTO = setTimeout(() => {
          this.charCount();
        }, 250);
      })
        .catch(e => this.$log.error(e));
    },
    charCount() {
      this.descCount = this.description.length;
    },
    saveNameChanges() {
      this.$validator.validate('sitename', this.sitename).then(async (result) => {
        if (result && this.sitename !== this.initialEdits.sitename) {
          // only update if there was a change
          this.smallLoaders.sitename = true;
          const notif = {
            id: this.guid(),
            type: 'success',
            message: 'Changes Successfully Saved!',
          };
          try {
            // update state
            this.$store.dispatch('setGlobalValue', {
              item: this.currentSite,
              key: 'alias',
              value: this.sitename,
            });
            // save to db
            await this._SEND([{
              name: 'updateSiteAttr',
              payload: {
                siteId: this.currentSite._id,
                attr: 'alias',
                value: this.sitename,
              },
            }]);
            this.$log.debug('successfully updated alias');
            // since each page uses the alias in the <title> tags, we need
            // to re-generate the entire site and publish it.
            await this.callBuilder({
              publish: 'site',
              site: {
                _id: this.currentSite._id,
              },
            });
            this.initialEdits.sitename = _.clone(this.sitename);
            this.toggleEditData('sitename');
          } catch (e) {
            this.$log.error('error: ', e);
            notif.type = 'error';
            notif.message = `${e.message || e}. This has been reported.`;
            await this._reportError({
              error: e,
              fn: 'saveNameChanges',
            });
          } finally {
            this.smallLoaders.sitename = false;
            this.$store.dispatch('addNotification', notif);
          }
        }
      });
    },
    async saveGaChanges() {
      if (this.analytics.trim() !== this.initialEdits.analytics) {
        // only update if there was a change
        this.smallLoaders.analytics = true;
        const notif = {
          id: this.guid(),
          type: 'success',
          message: 'Changes Successfully Saved!',
        };
        try {
          const value = this.analytics.trim() === '' ? false : this.analytics;
          // update state
          this.$store.dispatch('setGlobalValue', {
            item: this.currentSite,
            key: 'analytics',
            value,
          });
          // save to db
          await this._SEND([{
            name: 'updateSiteAttr',
            payload: {
              siteId: this.currentSite._id,
              attr: 'analytics',
              value,
            },
          }]);
          this.$log.debug('successfully updated analytics');
          // since each page uses analytics, we need to re-generate
          // the entire site and publish it.
          await this.callBuilder({
            publish: 'site',
            site: {
              _id: this.currentSite._id,
            },
          });
          this.initialEdits.analytics = _.clone(this.analytics);
          this.toggleEditData('analytics');
        } catch (e) {
          this.$log.error('error: ', e);
          notif.type = 'error';
          notif.message = `${e.message || e}. This has been reported.`;
          await this._reportError({
            error: e,
            fn: 'saveGaChanges',
          });
        } finally {
          this.smallLoaders.analytics = false;
          this.$store.dispatch('addNotification', notif);
          this._trackEvent({
            type: 'update_google_analytics',
            meta: {
              site_name: this.currentSite.alias,
              site_type: this.currentSite.type,
              site_pages: this.currentSite.pages.length,
              site_templates: this.currentSite.templates.length,
              site_draft: this.currentSite.drafts.length,
            },
          });
        }
      }
    },
    setTabs() {
      const disabled = !this._published;
      this.tabs = [
        {
          title: 'Domains',
          render: Domains,
          disabled,
        },
        {
          title: 'Form Submits',
          render: Forms,
          disabled,
        },
        {
          title: 'Plan',
          render: Options,
        },
        {
          title: 'Settings',
          render: Settings,
          disabled,
        },
      ];
      const { forecast, tier, cloudFront } = this.currentSite;
      if ((this._dev || this._storm) && forecast && tier !== 'Free' && cloudFront.id) {
        this.tabs.splice(1, 0, {
          title: 'Forecast',
          render: Forecast,
          disabled,
        });
      }
    },
    async launchEditor() {
      this.$router.push({ path: `/edit-page/${this.currentSite._id}` });
    },
    async sendDelete(site) {
      await this.$store.dispatch('deleteSite', { site });
    },
    toggleModal(type, c) {
      if (['suspendSite', 'deleteSite'].includes(type) && !this._siteOwner) {
        return;
      }
      this.modals[type] = !this.modals[type];
      const content = !c ? this.$refs[type] : this.$refs[type].$el;
      if (this.modals[type]) {
        this.modelMoveOut(content);
      } else {
        this.modalMoveIn(content);
      }
    },
    seperateCamalCase(str) {
      return str.replace(/([A-Z])/g, g => ` ${g[0].toLowerCase()}`);
    },
    formatErrors(errs) {
      return this.seperateCamalCase(errs.join(' '));
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/admin/websites/edit-site/index.js